import React, { useState } from "react";
import { Box, Switch, Tooltip, Typography } from "@mui/material";
import { toast } from "react-toastify";
import api from "../api";

/**
 * "Free sample" switch on Admin → Flash Cards, Game Topics and Worksheets. On: the item plays
 * on the public Flashcards / Games / Worksheets page with no account (PUT admin/free-samples).
 * Only active items can be switched on (`inactive` disables it with the reason), and the API
 * allows 10 per subject for each type; the 11th shows its message in a toast.
 *
 * @param {'flash_card'|'game_topic'|'worksheet'} type
 */
export default function FreeSampleSwitch({ type, id, value, inactive = false, onChange, compact = false }) {
  const [on, setOn] = useState(Boolean(value));
  const [saving, setSaving] = useState(false);

  // Keep in step when the list reloads.
  React.useEffect(() => setOn(Boolean(value)), [value]);

  const toggle = async (next) => {
    setSaving(true);
    setOn(next);
    try {
      const res = await api.put(`admin/free-samples/${type}/${id}`, { is_free_sample: next });
      toast.success(res.data?.message || (next ? "Now a free sample." : "No longer a free sample."));
      onChange?.(next);
    } catch (e) {
      setOn(!next);
      toast.error(e.response?.data?.message || "Could not change the free sample setting.");
    } finally {
      setSaving(false);
    }
  };

  const blocked = inactive && !on;
  const control = (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Switch
        size="small"
        color="success"
        checked={on}
        disabled={saving || blocked}
        onChange={(e) => toggle(e.target.checked)}
        inputProps={{ "aria-label": "Free sample on the public page" }}
      />
      {compact ? null : (
        <Typography component="span" sx={{ fontSize: 12, color: on ? "#05744F" : "text.secondary", fontWeight: on ? 700 : 400, whiteSpace: "nowrap" }}>
          {on ? "Free sample" : "Off"}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={
        blocked
          ? "Only active items can be free samples."
          : on
            ? "Plays on the public page with no account. Switch off to remove it."
            : "Make this a free sample on the public page (no account needed). Limited per subject (FREE_SAMPLE_PER_SUBJECT, default 10)."
      }
    >
      {control}
    </Tooltip>
  );
}
